import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { CoinProduct } from "../api/types";

export function CoinProductsPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<CoinProduct>("/admin/coin-products");
  const [editing, setEditing] = useState<Partial<CoinProduct> | null>(null);
  // The row's product_id at the time the edit modal opened — kept separate
  // from editing.product_id (which the admin can now freely retype) so the
  // PATCH still targets the right existing row even after it's been edited.
  const [originalProductId, setOriginalProductId] = useState<string | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CoinProduct | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToast();

  async function toggleActive(row: CoinProduct) {
    try {
      await apiRequest(`/admin/coin-products/${encodeURIComponent(row.product_id)}`, {
        method: "PATCH",
        body: { is_active: !row.is_active },
      });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update product", "error");
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await apiRequest(`/admin/coin-products/${encodeURIComponent(deleteTarget.product_id)}`, { method: "DELETE" });
      showToast("Coin product deleted", "success");
      setDeleteTarget(null);
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete", "error");
    } finally {
      setIsDeleting(false);
    }
  }

  async function save() {
    if (!editing?.product_id || !editing.coins_amount) {
      showToast("Play Console Product ID and coins amount are required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      if (isNew) {
        await apiRequest("/admin/coin-products", { method: "POST", body: editing });
      } else {
        // originalProductId (not editing.product_id) targets the existing
        // row — editing.product_id may itself be the new, renamed value.
        await apiRequest(`/admin/coin-products/${encodeURIComponent(originalProductId!)}`, { method: "PATCH", body: editing });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  const columns: Column<CoinProduct>[] = [
    { header: "Name", render: (row) => row.name ?? <span className="text-ink-faint">—</span> },
    { header: "Play Console Product ID", render: (row) => <span className="font-mono text-xs">{row.product_id}</span> },
    { header: "Coins Granted", render: (row) => <span className="font-semibold text-gold">{row.coins_amount.toLocaleString()}</span> },
    { header: "Price", render: (row) => row.price_display ?? "—" },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditing(row);
              setOriginalProductId(row.product_id);
              setIsNew(false);
            }}
          >
            Edit
          </Button>
          <Button size="sm" variant="secondary" onClick={() => toggleActive(row)}>
            {row.is_active ? "Disable" : "Enable"}
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDeleteTarget(row)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Coin Products">
      <div className="mb-4 flex justify-between">
        <p className="max-w-lg text-xs text-ink-faint">
          The product_id here must exactly match a real Play Console in-app product — the Worker looks up the coin
          amount to credit from this table, never from what the app claims.
        </p>
        <Button
          onClick={() => {
            setEditing({ is_active: true });
            setOriginalProductId(null);
            setIsNew(true);
          }}
        >
          + Add Product
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.product_id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No coin products configured"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => {
          setEditing(null);
          setOriginalProductId(null);
        }}
        title={isNew ? "Add Coin Product" : "Edit Coin Product"}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setEditing(null);
                setOriginalProductId(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={save} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Product Name (e.g. 1000 Coins)"
            value={editing?.name ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
          />
          <Input
            label="Play Console Product ID"
            value={editing?.product_id ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, product_id: e.target.value }))}
          />
          <Input
            label="Coins Granted"
            type="number"
            value={editing?.coins_amount ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, coins_amount: Number(e.target.value) }))}
          />
          <Input
            label="Price Display (e.g. $4.99)"
            value={editing?.price_display ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, price_display: e.target.value }))}
          />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        isDangerous
        title="Delete coin product?"
        confirmLabel="Delete"
        description={
          deleteTarget
            ? `"${deleteTarget.product_id}" will no longer be purchasable. Do not delete a product ID that's still live in the Play Console listing — disable it instead (edit -> uncheck Active) unless it's genuinely being retired.`
            : ""
        }
      />
    </AppShell>
  );
}
